import { MentorProfileDraft } from "@/lib/api/mentor-profile";
import { useAudioPlayback } from "@/lib/hooks/use-audio-playback";
import {
  dimUnplayedBars,
  formatAudioDuration,
  useAudioBlob,
  useStaticAudioWaveform,
} from "@/lib/hooks/use-audio-waveform";
import {
  useBatchSignedMedia,
  useSignedMedia,
} from "@/lib/hooks/use-signed-media";
import { isValidSpotifyLink } from "@/lib/mentor-profile/constants";
import { cn } from "@/lib/utils";
import { SpotifyVinyl } from "@somethings-ui/web";
import { ExternalLink, Mic, Music, Pause, Play } from "lucide-react";
import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { getInterestIcon } from "@/components/mentor-profile/interest-icons";
import { InterestPhotoStack } from "@/components/mentor-profile/interest-photo-stack";

const ACCENT = "#b164ff";
const VIBE_ROTATIONS = [-3.5, 2.5, -1.5, 3, -2.5];

export type PreviewRegion =
  | "identity"
  | "bio"
  | "spotify"
  | "media"
  | "vibe"
  | "interests"
  | "languages"
  | "all";

export interface PreviewFocus {
  region: PreviewRegion;
  zoom?: number;
}

interface MatchingProfilePreviewCardProps {
  draft: MentorProfileDraft;
  focus?: PreviewFocus;
}

function Skeleton({ className }: { className?: string }) {
  return <div className={cn("h-3 rounded-full bg-[#1f1e1d]/10", className)} />;
}

function StatusBar() {
  return (
    <div className="absolute inset-x-0 top-0 z-[15] flex items-center justify-between px-6 pt-3.5 text-white">
      <span className="text-[13px] font-semibold tabular-nums tracking-tight">
        9:41
      </span>
      <span className="flex items-end gap-[2px]">
        {[5, 7, 9, 11].map((h) => (
          <span
            key={h}
            className="w-[3px] rounded-full bg-white"
            style={{ height: h }}
          />
        ))}
      </span>
    </div>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-[#797774]">
      {children}
    </p>
  );
}

function LockedRow({
  icon,
  label,
  waveform,
}: {
  icon: ReactNode;
  label: string;
  waveform?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[#e7e0d5] bg-white p-3">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#f2efe9]">
        {icon}
      </span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="text-sm font-medium text-[#1f1e1d]/40">{label}</p>
        <Skeleton className="h-2 w-24" />
      </div>
      {waveform && (
        <div className="ml-auto flex items-end gap-0.5">
          {[8, 14, 7, 18, 10, 15, 6].map((h, i) => (
            <span
              key={i}
              className="w-1 rounded-full bg-[#1f1e1d]/15"
              style={{ height: h }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AudioPreviewRow({
  audioUrl,
  name,
}: {
  audioUrl: string;
  name: string;
}) {
  const { url: signedUrl } = useSignedMedia(audioUrl);
  const blob = useAudioBlob(signedUrl);
  const bars = useStaticAudioWaveform(blob);
  const { isPlaying, progressMs, durationMs, toggle } = useAudioPlayback(
    signedUrl ?? null,
  );
  const playedBars = dimUnplayedBars(bars, durationMs, progressMs);
  const durationLabel =
    durationMs > 0 ? ` · ${formatAudioDuration(durationMs)}` : "";

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[#e7e0d5] bg-white p-3">
      <button
        type="button"
        onClick={toggle}
        disabled={!signedUrl}
        aria-label={isPlaying ? "Pause intro" : "Play intro"}
        className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#b164ff] text-white disabled:opacity-60"
      >
        {isPlaying ? (
          <Pause className="size-4 fill-current" />
        ) : (
          <Play className="size-4 fill-current" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[#1f1e1d]">
          Hear from {name || "you"}
        </p>
        <p className="truncate text-xs capitalize text-[#797774]">
          audio intro{durationLabel}
        </p>
      </div>
      <div className="ml-auto flex items-end gap-0.5">
        {(playedBars ?? [10, 16, 8, 20, 12, 18, 7]).slice(0, 7).map((v, i) => (
          <span
            key={i}
            className="w-1 rounded-full bg-[#1f1e1d]/40"
            style={{
              height: playedBars
                ? `${Math.max(4, v * 20)}px`
                : `${[10, 16, 8, 20, 12, 18, 7][i]}px`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function DottedBust() {
  return (
    <div className="pointer-events-none absolute bottom-0 right-[-1%] z-0 h-[62%] w-[54%] text-white">
      <svg
        viewBox="0 0 100 120"
        preserveAspectRatio="xMidYMax meet"
        className="size-full"
        aria-hidden
      >
        <defs>
          <path
            id="bust"
            d="M50 4 C65 4 78 15 78 31 C78 42 72 49 65 51 C65 55 65 59 65 64 C78 68 88 75 93 87 C98 96 99 108 99 120 L99 132 L1 132 L1 120 C1 108 2 96 7 87 C12 75 22 68 35 64 C35 59 35 55 35 51 C28 49 22 42 22 31 C22 15 35 4 50 4 Z"
          />
          <pattern
            id="bustDotsLg"
            width="7"
            height="7"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="3.5" cy="3.5" r="1.2" fill="currentColor" />
          </pattern>
          <pattern
            id="bustDotsMd"
            width="7"
            height="7"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="3.5" cy="3.5" r="0.8" fill="currentColor" />
          </pattern>
          <pattern
            id="bustDotsSm"
            width="7"
            height="7"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="3.5" cy="3.5" r="0.45" fill="currentColor" />
          </pattern>
          <filter id="bustErodeMid">
            <feMorphology operator="erode" radius="2.5" />
          </filter>
          <filter id="bustErodeCore">
            <feMorphology operator="erode" radius="5.5" />
          </filter>
          <mask id="bustMaskMid">
            <use href="#bust" fill="#fff" filter="url(#bustErodeMid)" />
          </mask>
          <mask id="bustMaskCore">
            <use href="#bust" fill="#fff" filter="url(#bustErodeCore)" />
          </mask>
        </defs>
        <use href="#bust" fill="url(#bustDotsSm)" opacity="0.24" />
        <use
          href="#bust"
          fill="url(#bustDotsMd)"
          mask="url(#bustMaskMid)"
          opacity="0.42"
        />
        <use
          href="#bust"
          fill="url(#bustDotsLg)"
          mask="url(#bustMaskCore)"
          opacity="0.52"
        />
      </svg>
    </div>
  );
}

export function MatchingProfilePreviewCard({
  draft,
  focus,
}: MatchingProfilePreviewCardProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  const name = draft.display_name || draft.first_name || "";
  const cutoutSource = draft.match_card_photo_url?.trim();
  const fallbackPhotoSource = draft.profile_picture_url?.trim();
  const { url: cutoutPhotoUrl } = useSignedMedia(cutoutSource);
  const { url: fallbackPhotoUrl } = useSignedMedia(
    cutoutSource ? undefined : fallbackPhotoSource,
  );
  const photoUrl = cutoutPhotoUrl || fallbackPhotoUrl;
  const languages = draft.languages ?? [];
  const interests = (draft.interests ?? []).slice(0, 4);
  const interestImages = (draft.interest_images ?? []).filter(
    (image) => !!image.imageUrl,
  );
  const vibe = draft.vibe_images ?? [];
  const signedVibe = useBatchSignedMedia(vibe.map((image) => image.imageUrl));
  const signedInterest = useBatchSignedMedia(
    interestImages.map((image) => image.imageUrl),
  );
  const bioLength = draft.bio?.length ?? 0;

  const spotifyUrl = draft.favorite_music?.trim()
    ? draft.favorite_music
    : undefined;
  const spotifyMeta = draft.spotify_meta as
    | { title?: string; artist?: string; artworkUrl?: string }
    | undefined;
  const spotifyTitle = spotifyMeta?.title;
  const hasSpotify =
    !!spotifyUrl && (!!spotifyMeta || isValidSpotifyLink(spotifyUrl));

  const isZoomed = !!focus && focus.region !== "all";
  const highlighted = isZoomed
    ? ["identity", "bio"].includes(focus.region)
      ? "hero"
      : focus.region
    : null;

  const dim = (region: string) =>
    cn(
      "transition-[filter,opacity,transform] duration-500 ease-out",
      highlighted && highlighted !== region
        ? "scale-[0.98] opacity-40 blur-[3px]"
        : "opacity-100 blur-none",
    );

  const applyFocus = useCallback(() => {
    const inner = innerRef.current;
    const viewport = viewportRef.current;
    if (!inner || !viewport) return;

    if (!focus || focus.region === "all") {
      inner.style.transform = "none";
      return;
    }

    const isHero = ["identity", "bio"].includes(focus.region);
    const targetRegion = isHero ? "identity" : focus.region;
    const target =
      inner.querySelector<HTMLElement>(`[data-region="${targetRegion}"]`) ??
      inner.querySelector<HTMLElement>('[data-region="identity"]');
    if (!target) {
      inner.style.transform = "none";
      return;
    }

    let offset = 0;
    let node: HTMLElement | null = target;
    while (node && node !== inner) {
      offset += node.offsetTop;
      node = node.offsetParent as HTMLElement | null;
    }

    const zoom = focus.zoom ?? 1;
    const viewportH = viewport.clientHeight;
    const anchorFromTop = isHero ? 0 : 0.18 * viewportH;
    const rawAnchor = anchorFromTop / zoom - offset;
    const anchor = Math.min(0, rawAnchor);
    inner.style.transform = `scale(${zoom}) translateY(${anchor}px)`;
  }, [focus]);

  useEffect(() => {
    applyFocus();
  }, [
    applyFocus,
    name,
    bioLength,
    interests.length,
    interestImages.length,
    languages.length,
    hasSpotify,
    spotifyTitle,
    draft.profile_audio_url,
    photoUrl,
    vibe.length,
  ]);

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => applyFocus());
    observer.observe(inner);
    window.addEventListener("resize", applyFocus);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", applyFocus);
    };
  }, [applyFocus]);

  return (
    <div
      ref={viewportRef}
      className={cn(
        "relative h-full bg-white text-[#1f1e1d]",
        isZoomed
          ? "overflow-hidden"
          : "overflow-y-auto overscroll-y-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
      )}
    >
      <div
        ref={innerRef}
        className="relative origin-top transition-transform will-change-transform [transition-duration:700ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
      >
        <div
          data-region="identity"
          className={cn("relative overflow-hidden px-5 pt-16 min-h-[468px]", dim("hero"))}
          style={{ background: ACCENT }}
        >
          <StatusBar />
          {!photoUrl && <DottedBust />}
          {photoUrl && (
            <div className="pointer-events-none absolute bottom-0 right-[-4%] z-0 h-[78%] w-[72%]">
              <img
                src={photoUrl}
                alt={name}
                className="absolute inset-0 size-full object-contain object-bottom"
              />
            </div>
          )}

          <div className="relative z-10 flex flex-col gap-3.5 text-white">
            <div className="text-center">
              {name ? (
                <h2 className="text-[34px] font-bold leading-[1.1] tracking-[-0.02em] text-white [overflow-wrap:anywhere]">
                  {name}
                </h2>
              ) : (
                <Skeleton className="mx-auto h-7 w-40 bg-white/30" />
              )}
              {draft.pronouns ? (
                <p className="mt-1 text-sm text-white/80">{draft.pronouns}</p>
              ) : (
                <Skeleton className="mx-auto mt-2 h-2.5 w-24 bg-white/25" />
              )}
            </div>

            <div
              data-region="bio"
              className="w-full rounded-[16px] rounded-bl-[4px] bg-white/15 px-3.5 py-2.5 text-left backdrop-blur-sm"
            >
              {draft.bio ? (
                <p className="whitespace-pre-wrap text-[15px] leading-[1.4] text-white [overflow-wrap:anywhere]">
                  {draft.bio}
                </p>
              ) : (
                <div className="space-y-1.5 py-0.5">
                  <Skeleton className="h-2.5 w-full bg-white/30" />
                  <Skeleton className="h-2.5 w-[78%] bg-white/30" />
                </div>
              )}
            </div>

            <div className="relative flex items-stretch gap-2">
              <div className="relative z-10 flex flex-1 flex-col items-start gap-2 pb-7">
                {interests.length > 0
                  ? interests.map((interest) => {
                      const Icon = getInterestIcon(interest);
                      return (
                        <span
                          key={interest}
                          className="inline-flex items-center gap-2 rounded-full border border-white/45 bg-[#b164ff]/40 px-3 py-1.5 text-sm text-white backdrop-blur-sm"
                        >
                          <Icon
                            className="size-4 shrink-0"
                            strokeWidth={1.75}
                          />
                          {interest}
                        </span>
                      );
                    })
                  : [44, 60, 52].map((w, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-2 rounded-full border border-dashed border-white/30 px-3 py-1.5"
                      >
                        <span className="size-4 rounded-full bg-white/30" />
                        <span
                          className="block h-2.5 rounded-full bg-white/30"
                          style={{ width: w }}
                        />
                      </span>
                    ))}
              </div>
              {/* Spacer keeps hero tall so the absolute cutout has room to render large */}
              <div
                className="min-h-[280px] w-[52%] shrink-0 self-stretch"
                aria-hidden
              />
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-6 px-5 pb-12 pt-6">
          <div
            data-region="spotify"
            className={cn("relative z-10 -mt-16", dim("spotify"))}
          >
            {hasSpotify ? (
              <div className="flex items-center gap-3">
                <SpotifyVinyl
                  spotifyUrl={spotifyUrl ?? ""}
                  previewUrl={draft.spotify_preview_url}
                  size="sm"
                  className="[&>p]:hidden"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#797774]">
                    On repeat
                  </p>
                  <p className="truncate text-[15px] font-semibold text-[#1f1e1d]">
                    {spotifyTitle || "Your track"}
                  </p>
                  <a
                    href={spotifyUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-0.5 inline-flex items-center gap-1 text-xs text-[#797774] transition-colors hover:text-[#1f1e1d]"
                  >
                    Open in Spotify <ExternalLink className="size-3" />
                  </a>
                </div>
              </div>
            ) : (
              <LockedRow
                icon={<Music className="size-5 text-[#1f1e1d]/30" />}
                label="On repeat"
              />
            )}
          </div>

          <div data-region="media" className={dim("media")}>
            {draft.profile_audio_url ? (
              <AudioPreviewRow audioUrl={draft.profile_audio_url} name={name} />
            ) : (
              <LockedRow
                icon={<Mic className="size-5 text-[#1f1e1d]/30" />}
                label={`Hear from ${name || "you"}`}
                waveform
              />
            )}
          </div>

          <div data-region="vibe" className={dim("vibe")}>
            <SectionHeading>My vibe</SectionHeading>
            <div className="-mx-5 flex gap-3 overflow-x-auto px-5 pb-3 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {vibe.length > 0
                ? vibe.map((image, i) => (
                    <div
                      key={image.imageUrl}
                      className="aspect-[3/4] w-24 shrink-0 overflow-hidden rounded-xl border-2 border-white bg-[#f5f3f0] shadow-[0_4px_14px_rgba(31,30,29,0.12)]"
                      style={{
                        transform: `rotate(${VIBE_ROTATIONS[i % VIBE_ROTATIONS.length]}deg)`,
                      }}
                    >
                      {signedVibe[i]?.url ? (
                        <img
                          src={signedVibe[i].url}
                          alt=""
                          className="size-full object-cover"
                        />
                      ) : null}
                    </div>
                  ))
                : [0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="aspect-[3/4] w-24 shrink-0 rounded-xl border-2 border-dashed border-[#1f1e1d]/15 bg-[#1f1e1d]/[0.03]"
                      style={{
                        transform: `rotate(${VIBE_ROTATIONS[i % VIBE_ROTATIONS.length]}deg)`,
                      }}
                    />
                  ))}
            </div>
          </div>

          <div data-region="interests" className={dim("interests")}>
            <SectionHeading>My interests</SectionHeading>
            <InterestPhotoStack
              images={interestImages.map((image, i) => ({
                interest: image.interest,
                imageUrl: image.imageUrl,
                signedUrl: signedInterest[i]?.url,
              }))}
            />
          </div>

          <div data-region="languages" className={dim("languages")}>
            <SectionHeading>Languages</SectionHeading>
            {languages.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {languages.map((language) => (
                  <span
                    key={language}
                    className="rounded-full bg-[#f5f3f0] px-3 py-1 text-sm text-[#1f1e1d]"
                  >
                    {language}
                  </span>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {[56, 72, 48].map((w, i) => (
                  <span
                    key={i}
                    className="h-7 rounded-full bg-[#1f1e1d]/[0.08]"
                    style={{ width: w }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
